/**
 * useAuth.js - React 19 authentication hook (Fixed)
 *
 * This hook provides authentication functionality using React 19 patterns
 * with proper async handling for client-side components.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import tokenService from '../services/auth';

/**
 * Hook for authentication state and operations
 *
 * @returns {Object} Authentication state and methods
 * @returns {string} .token - The current authentication token
 * @returns {boolean} .isAuthenticated - Whether user is authenticated
 * @returns {boolean} .loading - Whether auth is currently loading
 * @returns {Function} .getAuthHeaders - Get headers for authenticated requests
 * @returns {Function} .refreshToken - Force token refresh
 * @returns {Function} .logout - Clear authentication
 */
export function useAuth() {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch token on mount
  useEffect(() => {
    const fetchToken = async () => {
      try {
        setLoading(true);
        const authToken = await tokenService.getToken();
        setToken(authToken);
        setError(null);
      } catch (err) {
        console.error('Error fetching token:', err);
        setError('Something went wrong with your session. Please try again.');
        setToken(null);
      } finally {
        setLoading(false);
      }
    };

    fetchToken();
  }, []);

  // Memoized auth state
  const isAuthenticated = useMemo(() => !!token, [token]);

  // Memoized auth actions to prevent unnecessary re-renders
  const getAuthHeaders = useCallback(async () => {
    try {
      // If we don't have a token, try to get one
      if (!token) {
        const freshToken = await tokenService.getToken();
        setToken(freshToken);
        return tokenService.getAuthHeaders();
      }
      return tokenService.getAuthHeaders();
    } catch (err) {
      console.error('Error getting auth headers:', err);
      setError('Something went wrong with your session. Please try again.');
      throw err;
    }
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      setLoading(true);
      // TokenService no longer makes API calls, so we just get the current token
      const newToken = await tokenService.getToken();
      setToken(newToken);
      setError(null);
      return newToken;
    } catch (err) {
      console.error('Error getting token:', err);
      setError('Something went wrong with your session. Please try again.');
      setToken(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    tokenService.clearToken();
    setToken(null);
    setError(null);
  }, []);

  return {
    token,
    isAuthenticated,
    loading,
    error,
    getAuthHeaders,
    refreshToken,
    logout,
  };
}

export default useAuth;
